import { Injectable, computed, inject } from '@angular/core';

import { TenancyService } from '../tenancy/tenancy.service';
import { PermissionService } from '../permissions/permission.service';
import { environment } from '../../../environments/environment';

/**
 * Rail grouping — console redesign (Cafooze POS prototype): "Daily work" for
 * the screens a shift lives in, "Manage" for set-up and back-office screens.
 * A purely presentational bucket carrying no gating of its own; every item's
 * own permission/rootOnly/managerOrAdmin/adminOnly/previewOnly field is what
 * decides visibility. 'Platform' holds Root's Licenses link only.
 */
export type NavGroup = 'Daily work' | 'Manage' | 'Platform';

/** Rendered below the group sections, separated by a rule — AI Assistant / Organization / Settings / Help & support. */
export type NavFooterGroup = 'Footer' | 'PlatformFooter';

export interface NavItem {
  path: string;
  /** Present only on an item whose route already carries a `?view=` sub-view. */
  queryParams?: Record<string, string>;
  label: string;
  icon: string;
  group: NavGroup | NavFooterGroup;
  /** Permission key required to see the link; undefined means "any signed-in member". */
  permission?: string;
  rootOnly?: boolean;
  /** Admin/manager only, regardless of role-permission assignment — see requireManagerOrAdmin. */
  managerOrAdmin?: boolean;
  /** Admin only — narrower than managerOrAdmin, for things a Manager genuinely can't do (e.g. branding). */
  adminOnly?: boolean;
  /**
   * DEV-only preview module (see requirePreview in permission.service.ts).
   * Hidden whenever environment.previewFeatures is false.
   */
  previewOnly?: boolean;
  /**
   * An escape hatch for the rare item whose visibility is a genuine
   * disjunction of roles/permissions (e.g. Marketing: admin OR manager OR
   * manage_rewards OR manage_marketing — matches requireMarketingAccess).
   * Evaluated alongside every other check (all must pass).
   */
  visibleWhen?: (ctx: { isAdmin: boolean; role: string | null; can: (permission: string) => boolean }) => boolean;
}

/** Fixed render order for the group sections. */
const NAV_GROUP_ORDER: NavGroup[] = ['Platform', 'Daily work', 'Manage'];

/**
 * Every constraint an item declares must pass. A link can require both a
 * role (managerOrAdmin) and a plan entitlement (permission) at once.
 *
 * Labels follow the redesign's plain wording (Stock, Expenses, Staff,
 * Reports); routes and gates are unchanged.
 */
const NAV_ITEMS: NavItem[] = [
  { path: '/root-console', label: 'Licenses', icon: 'apartment', group: 'Platform', rootOnly: true },

  // ------------------------------------------------------------ Daily work
  { path: '/dashboard', label: 'Home', icon: 'home', group: 'Daily work' },
  // Floor, Table requests, Reservations, QR codes and Guest access are tabs
  // on one page (TablesHub). Open to any signed-in staff; the page itself
  // hides the tabs a role can't use.
  { path: '/tables', label: 'Tables', icon: 'table_restaurant', group: 'Daily work' },
  {
    path: '/orders',
    label: 'Orders',
    icon: 'receipt_long',
    group: 'Daily work',
    previewOnly: true,
    permission: 'manage_orders',
  },
  // Guarded on manage_orders, not the similarly-named manage_billing — that
  // key means "manage this venue's own subscription" (admin territory).
  // Settling a guest's bill runs under requirePreview + manage_orders, with
  // finer per-action checks inside the Billing screens.
  {
    path: '/billing',
    label: 'Billing',
    icon: 'payments',
    group: 'Daily work',
    previewOnly: true,
    permission: 'manage_orders',
  },
  { path: '/tasks', label: 'Tasks', icon: 'checklist', group: 'Daily work' },

  // ---------------------------------------------------------------- Manage
  {
    path: '/menu',
    label: 'Menu',
    icon: 'restaurant_menu',
    group: 'Manage',
    previewOnly: true,
    permission: 'view_menu',
  },
  {
    path: '/inventory',
    label: 'Stock',
    icon: 'inventory_2',
    group: 'Manage',
    previewOnly: true,
  },
  // Purchases and Expenses live in one hub, /spend. visibleWhen rather than
  // a single `permission`: Purchases needs manage_inventory, Expenses needs
  // manage_expenses — genuinely independent grants. SpendHub itself shows
  // only the tabs the viewer actually has.
  {
    path: '/spend',
    label: 'Expenses',
    icon: 'account_balance_wallet',
    group: 'Manage',
    previewOnly: true,
    visibleWhen: ({ isAdmin, can }) => isAdmin || can('manage_inventory') || can('manage_expenses'),
  },
  { path: '/team', label: 'Staff', icon: 'groups', group: 'Manage' },
  {
    path: '/insights',
    label: 'Reports',
    icon: 'analytics',
    group: 'Manage',
    previewOnly: true,
    permission: 'view_flow_reports',
  },
  // visibleWhen mirrors requireMarketingAccess's own disjunction exactly
  // (permission.service.ts).
  {
    path: '/marketing',
    label: 'Marketing',
    icon: 'campaign',
    group: 'Manage',
    previewOnly: true,
    visibleWhen: ({ isAdmin, role, can }) =>
      isAdmin || role === 'manager' || can('manage_rewards') || can('manage_marketing'),
  },

  // ---------------------------------------------------------------- Footer
  {
    path: '/ai',
    label: 'AI Assistant',
    icon: 'smart_toy',
    group: 'Footer',
    previewOnly: true,
    permission: 'use_venue_ai',
  },
  // SettingsHub gates each tab internally — this link only needs the hub's
  // own weakest-tab guard (requireManagerOrAdmin, same as settings.routes.ts).
  {
    path: '/settings',
    label: 'Settings',
    icon: 'settings',
    group: 'Footer',
    managerOrAdmin: true,
  },
];

/**
 * The one place role ∩ plan ∩ preview nav gating is expressed.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly tenancy = inject(TenancyService);
  private readonly permissions = inject(PermissionService);

  readonly items = computed(() =>
    NAV_ITEMS.filter((item) => {
      if (item.previewOnly && !environment.previewFeatures) return false;
      if (item.rootOnly) return this.tenancy.isRoot();
      if (this.tenancy.isRoot()) return false; // Root administers licenses, not venue operations
      if (!this.tenancy.role()) return false;
      if (item.managerOrAdmin && !(this.tenancy.isAdmin() || this.tenancy.role() === 'manager')) {
        return false;
      }
      if (item.adminOnly && !this.tenancy.isAdmin()) return false;
      if (item.permission && !this.permissions.can(item.permission)) return false;
      if (
        item.visibleWhen &&
        !item.visibleWhen({
          isAdmin: this.tenancy.isAdmin(),
          role: this.tenancy.role(),
          can: (permission) => this.permissions.can(permission),
        })
      ) {
        return false;
      }
      return true;
    })
  );

  /**
   * The filtered set bucketed into labelled rail sections — a section with
   * nothing visible in it is omitted entirely.
   */
  readonly groups = computed(() => {
    const items = this.items();
    return NAV_GROUP_ORDER.map((group) => ({
      group,
      items: items.filter((item) => item.group === group),
    })).filter((section) => section.items.length > 0);
  });

  /**
   * Rendered below a rule at the bottom of the rail — AI Assistant /
   * Settings, plus "Organization" for any org member. Organization is built
   * here rather than as a NAV_ITEMS entry because its path is per-user
   * (/o/{orgId}); invisible for single-venue accounts.
   */
  readonly footerItems = computed(() => {
    const items = this.items().filter((item) => item.group === 'Footer');
    const orgId = this.tenancy.orgId();
    if (!orgId || this.tenancy.isRoot()) return items;
    return [
      { path: `/o/${orgId}`, label: 'Organization', icon: 'corporate_fare', group: 'Footer' as const },
      ...items,
    ];
  });
}
